import { usePrivy } from '@privy-io/react-auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getPrivyPrimaryAddress } from '@/hooks';
import { primaryPrivateV2Api } from '@/lib/api-clients/rest-client';
import { NotificationChannelType } from '@/types';

import {
  getNotificationsQueryKey,
  NotificationsSettings,
} from './get-notifications-settings';

type Body = {
  channelType: NotificationChannelType;
  enable: boolean;
  notificationType: string;
};

const toggleNotification = (body: Body) =>
  primaryPrivateV2Api.post('/v2/notifications/enable', body);

export default function useToggleNotification() {
  const { user } = usePrivy();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Body) => toggleNotification(body),
    onSuccess: (_, variables) => {
      const address = getPrivyPrimaryAddress(user);
      queryClient.setQueryData(
        getNotificationsQueryKey(address),
        (prev?: NotificationsSettings) => {
          if (!prev) return prev;
          return {
            ...prev,
            channels: prev.channels.map((channel) => {
              const index = channel.preferences.findIndex(
                (preference) =>
                  preference.notificationType === variables.notificationType,
              );
              if (index === -1) {
                return {
                  ...channel,
                  preferences: [
                    ...channel.preferences,
                    {
                      isEnabled: variables.enable,
                      notificationType: variables.notificationType,
                    },
                  ],
                };
              } else {
                const preferences = [...channel.preferences];
                preferences[index] = {
                  ...preferences[index],
                  isEnabled: variables.enable,
                };
                return {
                  ...channel,
                  preferences,
                };
              }
            }),
          } as NotificationsSettings;
        },
      );
    },
  });
}
